/**
 * PinCodesEditor — admin sub-tab inside Settings → "PIN codes".
 *
 * Currently exposes:
 *   • PIN for customer delete — empty value disables the gate
 *
 * Backend:
 *   GET /api/admin/pins         → { customer_delete: "1234" | "" }
 *   PUT /api/admin/pins         body { customer_delete: "1234" }
 */
import React, { useEffect, useState } from 'react';
import { Lock, Save, Loader2, Eye, EyeOff, ShieldCheck, ShieldOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PinCodesEditor({ token }) {
  const [pin, setPin] = useState('');
  const [original, setOriginal] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/pins`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        setPin(d.customer_delete || '');
        setOriginal(d.customer_delete || '');
      } catch (e) {
        setError(e.message || String(e));
      } finally { setLoading(false); }
    })();
  }, [token]);

  const dirty = pin !== original;
  const validPin = pin === '' || (/^\d{3,8}$/.test(pin));

  const save = async () => {
    setError(''); setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/pins`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_delete: pin }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { setError(body.detail || `HTTP ${r.status}`); return; }
      setOriginal(pin);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      setError(e.message || 'Network error');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="pin-codes-editor">
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <Lock className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-900">
          <p className="font-semibold mb-1">Admin PIN codes</p>
          <p>PIN codes guard destructive admin actions. Each PIN must be 3–8 digits. An empty value disables the gate.</p>
        </div>
      </div>

      {/* Customer-delete PIN card */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-slate-800">PIN for customer delete</h3>
            <p className="text-xs text-slate-500 mt-0.5">Required when deleting a customer account from the Customers page.</p>
          </div>
          {original ? (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Active
            </span>
          ) : (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-slate-100 text-slate-500 inline-flex items-center gap-1">
              <ShieldOff className="w-3 h-3" /> Disabled
            </span>
          )}
        </div>

        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Current PIN</label>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  inputMode="numeric"
                  autoComplete="off"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 8))}
                  placeholder="(empty disables the PIN gate)"
                  data-testid="pin-customer-delete-input"
                  className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-2 top-1.5 w-7 h-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500"
                  title={show ? 'Hide' : 'Show'}
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {!validPin && (
                <p className="text-xs text-red-600 mt-1">PIN must be 3–8 digits</p>
              )}
            </div>
            <button
              onClick={save}
              disabled={!dirty || !validPin || saving}
              data-testid="pin-customer-delete-save"
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-bold hover:shadow-md disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        )}

        {error && (
          <p className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</p>
        )}
        {savedFlash && (
          <p className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="pin-saved-flash">
            Saved.
          </p>
        )}
      </div>
    </div>
  );
}
